/**
 * Miro's keyboard shortcuts (https://miro.com/shortcuts/) as one definition
 * shared by the whiteboard apps (BOARD_KEYMAP) and the PDF / slides markup
 * editors (EDITOR_KEYMAP: the same Miro keys plus a "Markup tools" group).
 *
 * Framework-free: a binding list, a combo matcher, a typing guard and a
 * capture-phase keydown handler that runs app-supplied commands. A binding
 * with no handler in an app passes through untouched, so the library's own
 * shortcut (copy, undo, group, …) keeps working.
 *
 * Combo syntax: `mod+shift+g`, `alt+1`, `pageup`, `?`. `mod` is ⌘ on Apple
 * platforms and Ctrl elsewhere. Keys are matched by physical letter/digit
 * (`KeyboardEvent.code`), so ⌥1 on a Mac (which types "¡") still matches.
 */

export type Platform = 'mac' | 'other';
export type KeyGroup = 'tools' | 'markup' | 'estimate' | 'navigation' | 'objects' | 'universal';

export type CommandId =
  | 'tool.select'
  | 'tool.hand'
  | 'tool.text'
  | 'tool.sticky'
  | 'tool.shapes'
  | 'tool.rectangle'
  | 'tool.oval'
  | 'tool.connector'
  | 'tool.card'
  | 'tool.pen'
  | 'tool.eraser'
  | 'tool.comment'
  | 'tool.frame'
  | 'view.minimap'
  | 'view.grid'
  | 'app.palette'
  | 'app.shortcuts'
  | 'nav.arrows'
  | 'nav.zoomIn'
  | 'nav.zoomOut'
  | 'nav.zoom100'
  | 'nav.zoomFit'
  | 'nav.zoomSelection'
  | 'nav.spacePan'
  | 'nav.search'
  | 'obj.next'
  | 'obj.prev'
  | 'obj.edit'
  | 'obj.escape'
  | 'obj.copy'
  | 'obj.paste'
  | 'obj.cut'
  | 'obj.duplicate'
  | 'obj.altDragDuplicate'
  | 'obj.duplicateDirection'
  | 'obj.selectAll'
  | 'obj.delete'
  | 'obj.group'
  | 'obj.ungroup'
  | 'obj.lock'
  | 'obj.front'
  | 'obj.back'
  | 'obj.undo'
  | 'obj.redo'
  // Boards: design estimate
  | 'estimate.fixture'
  | 'estimate.quickAdd'
  | 'estimate.panel'
  | 'estimate.favourite'
  | 'estimate.linear'
  | 'estimate.area'
  | 'estimate.calibrate'
  | 'estimate.dimension'
  | 'estimate.mirror'
  | 'estimate.zone'
  | 'estimate.printedScale'
  | 'estimate.lockDrawing'
  | 'estimate.units'
  | 'estimate.editPoints'
  | 'estimate.cancelRun'
  | 'estimate.undoPoint'
  // Markup editors only
  | 'tool.line'
  | 'tool.callout'
  | 'tool.stamp'
  | 'tool.polygon'
  | 'tool.polyline'
  | 'intent.region'
  | 'intent.slide'
  | 'page.prev'
  | 'page.next';

export interface KeyBinding {
  id: CommandId;
  label: string;
  group: KeyGroup;
  /** Matchable combos. Empty for gestures that are only listed (Space+drag, ⌥+drag). */
  combos: string[];
  /** Shown instead of the formatted combos (gestures, arrow families). */
  display?: string;
}

const ARROWS = ['arrowup', 'arrowdown', 'arrowleft', 'arrowright'];

export const BOARD_KEYMAP: readonly KeyBinding[] = [
  // Tools
  { id: 'tool.select', label: 'Select tool', group: 'tools', combos: ['v'] },
  { id: 'tool.hand', label: 'Hand (move canvas)', group: 'tools', combos: ['h'] },
  { id: 'tool.text', label: 'Text', group: 'tools', combos: ['t'] },
  { id: 'tool.sticky', label: 'Sticky note', group: 'tools', combos: ['n'] },
  { id: 'tool.shapes', label: 'Shapes (press again to cycle)', group: 'tools', combos: ['s'] },
  { id: 'tool.rectangle', label: 'Rectangle', group: 'tools', combos: ['r'] },
  { id: 'tool.oval', label: 'Oval', group: 'tools', combos: ['o'] },
  { id: 'tool.connector', label: 'Connection line', group: 'tools', combos: ['l'] },
  { id: 'tool.card', label: 'Card', group: 'tools', combos: ['d'] },
  { id: 'tool.pen', label: 'Pen', group: 'tools', combos: ['p'] },
  { id: 'tool.eraser', label: 'Eraser', group: 'tools', combos: ['e'] },
  { id: 'tool.comment', label: 'Comment', group: 'tools', combos: ['c'] },
  { id: 'tool.frame', label: 'Frame', group: 'tools', combos: ['f'] },
  { id: 'view.minimap', label: 'Minimap', group: 'tools', combos: ['m'] },
  { id: 'view.grid', label: 'Toggle grid', group: 'tools', combos: ['g'] },
  { id: 'app.palette', label: 'Command palette', group: 'tools', combos: ['mod+k'] },
  { id: 'app.shortcuts', label: 'Keyboard shortcuts', group: 'tools', combos: ['f1', '?'] },
  // Design estimate (keys Miro leaves free)
  { id: 'estimate.fixture', label: 'Fixture tool (place the armed fixture type)', group: 'estimate', combos: ['i'] },
  { id: 'estimate.quickAdd', label: 'Quick add a fixture ("LD 6 downlight 12W $180")', group: 'estimate', combos: ['/'] },
  { id: 'estimate.panel', label: 'Estimate panel (fixtures · budget · BOM)', group: 'estimate', combos: ['shift+b'] },
  { id: 'estimate.favourite', label: 'Pick fixture type 1–9 (while the fixture tool is on)', group: 'estimate', combos: ['1', '2', '3', '4', '5', '6', '7', '8', '9'], display: '1 – 9' },
  { id: 'estimate.linear', label: 'Measure a run (click points, double-click / Enter to finish)', group: 'estimate', combos: ['w'] },
  { id: 'estimate.area', label: 'Measure an area (click corners, double-click / Enter to close)', group: 'estimate', combos: ['a'] },
  { id: 'estimate.calibrate', label: 'Set the scale of a drawing (two clicks + real distance)', group: 'estimate', combos: ['shift+c'] },
  { id: 'estimate.dimension', label: 'Dimension: click both ends — its length on the scaled drawing, not counted in the BOM', group: 'estimate', combos: ['shift+d'] },
  { id: 'estimate.zone', label: 'Zone: drag a freehand shape (or click points, Enter to close) — fixtures inside get that ZONE in the workbook', group: 'estimate', combos: ['z'] },
  { id: 'estimate.mirror', label: 'Mirror the selection: click two points for the axis (Shift: level / plumb, Alt-click: vertical through one point)', group: 'estimate', combos: ['shift+m'] },
  { id: 'estimate.printedScale', label: 'Printed scale of a drawing (1/8" = 1\'-0", 1:200…)', group: 'estimate', combos: [], display: 'toolbar 📐' },
  { id: 'estimate.lockDrawing', label: 'Lock / unlock a drawing (the scale stays)', group: 'estimate', combos: [], display: 'toolbar 🔒' },
  { id: 'estimate.units', label: "Units: feet-inches · decimal feet · metres", group: 'estimate', combos: [], display: 'toolbar ft' },
  { id: 'estimate.editPoints', label: "Edit a line's points — or double-click the line; drag the dots between points to add a bend", group: 'estimate', combos: ['mod+enter'] },
  { id: 'estimate.cancelRun', label: 'Cancel the run or area being drawn', group: 'estimate', combos: [], display: 'Esc' },
  { id: 'estimate.undoPoint', label: 'Remove the last point of the run being drawn', group: 'estimate', combos: [], display: 'Backspace' },
  // Navigation
  { id: 'nav.arrows', label: 'Move items / move canvas', group: 'navigation', combos: [...ARROWS, ...ARROWS.map((a) => `shift+${a}`)], display: '← → ↑ ↓' },
  { id: 'nav.zoomIn', label: 'Zoom in', group: 'navigation', combos: ['mod+='] },
  { id: 'nav.zoomOut', label: 'Zoom out', group: 'navigation', combos: ['mod+-'] },
  { id: 'nav.zoom100', label: 'Zoom to 100%', group: 'navigation', combos: ['mod+0'] },
  { id: 'nav.zoomFit', label: 'Zoom to fit', group: 'navigation', combos: ['alt+1'] },
  { id: 'nav.zoomSelection', label: 'Zoom to selected item', group: 'navigation', combos: ['alt+2'] },
  { id: 'nav.spacePan', label: 'Move canvas', group: 'navigation', combos: [], display: 'Space + drag' },
  { id: 'nav.search', label: 'Search', group: 'navigation', combos: ['mod+f'] },
  // Board objects
  { id: 'obj.next', label: 'Next object (top-left → bottom-right)', group: 'objects', combos: ['tab'] },
  { id: 'obj.prev', label: 'Previous object', group: 'objects', combos: ['shift+tab'] },
  { id: 'obj.edit', label: 'Edit selected object', group: 'objects', combos: ['enter'] },
  { id: 'obj.escape', label: 'Stop editing · deselect · select tool', group: 'objects', combos: ['escape'] },
  // Universal
  { id: 'obj.copy', label: 'Copy', group: 'universal', combos: ['mod+c'] },
  { id: 'obj.paste', label: 'Paste', group: 'universal', combos: ['mod+v'] },
  { id: 'obj.cut', label: 'Cut', group: 'universal', combos: ['mod+x'] },
  { id: 'obj.duplicate', label: 'Duplicate', group: 'universal', combos: ['mod+d'] },
  { id: 'obj.altDragDuplicate', label: 'Duplicate', group: 'universal', combos: [], display: 'alt + drag' },
  { id: 'obj.duplicateDirection', label: 'Duplicate in a direction', group: 'universal', combos: ARROWS.map((a) => `alt+${a}`), display: 'alt + ← → ↑ ↓' },
  { id: 'obj.selectAll', label: 'Select all', group: 'universal', combos: ['mod+a'] },
  { id: 'obj.delete', label: 'Delete', group: 'universal', combos: ['delete', 'backspace'] },
  { id: 'obj.group', label: 'Group', group: 'universal', combos: ['mod+g'] },
  { id: 'obj.ungroup', label: 'Ungroup', group: 'universal', combos: ['mod+shift+g'] },
  { id: 'obj.lock', label: 'Lock / unlock', group: 'universal', combos: ['mod+shift+l'] },
  { id: 'obj.front', label: 'Bring to front', group: 'universal', combos: ['pageup'] },
  { id: 'obj.back', label: 'Send to back', group: 'universal', combos: ['pagedown'] },
  { id: 'obj.undo', label: 'Undo', group: 'universal', combos: ['mod+z'] },
  { id: 'obj.redo', label: 'Redo', group: 'universal', combos: ['mod+shift+z'] },
];

export const GROUP_TITLES: Record<KeyGroup, string> = {
  tools: 'Everyday shortcuts',
  markup: 'Markup tools',
  estimate: 'Measure & estimate',
  navigation: 'Navigating your board',
  objects: 'Navigating board objects',
  universal: 'Universal shortcuts',
};

const DIGITS = ['1', '2', '3', '4', '5', '6', '7', '8', '9'];

/** Board keys the markup editors don't have (no cards, frames, eraser, grid, minimap, grouping or locking). */
const NOT_IN_EDITORS = new Set<CommandId>([
  'tool.card', 'tool.eraser', 'tool.frame', 'view.minimap', 'view.grid', 'obj.group', 'obj.ungroup', 'obj.lock',
  'estimate.fixture', 'estimate.quickAdd', 'estimate.panel', 'estimate.favourite',
  'estimate.linear', 'estimate.area', 'estimate.calibrate', 'estimate.dimension', 'estimate.mirror', 'estimate.zone', 'estimate.printedScale', 'estimate.lockDrawing', 'estimate.units',
  'estimate.editPoints', 'estimate.cancelRun', 'estimate.undoPoint',
]);

/** Labels that read differently on a drawing or a slide. */
const EDITOR_LABELS: Partial<Record<CommandId, string>> = {
  'tool.text': 'Free text',
  'tool.shapes': 'Shapes (press again: rectangle → ellipse → cloud)',
  'tool.oval': 'Ellipse',
  'tool.connector': 'Arrow (connection line)',
  'tool.pen': 'Pen (ink)',
  'tool.comment': 'Comment sticky',
  'nav.arrows': 'Nudge selection / move canvas',
  'nav.zoomFit': 'Fit page width',
  'nav.search': 'Search markups',
  'obj.next': 'Next markup on this page',
  'obj.prev': 'Previous markup on this page',
  'obj.selectAll': 'Select all on this page',
  'obj.front': 'Bring to front (selection) · else previous page',
  'obj.back': 'Send to back (selection) · else next page',
};

/** Markup-only tools, on keys Miro leaves free. */
const MARKUP_TOOLS: KeyBinding[] = [
  { id: 'tool.line', label: 'Line', group: 'markup', combos: ['shift+l'] },
  { id: 'tool.callout', label: 'Callout', group: 'markup', combos: ['k'] },
  { id: 'tool.stamp', label: 'Stamp', group: 'markup', combos: ['u'] },
  { id: 'tool.polygon', label: 'Polygon (drawings)', group: 'markup', combos: ['shift+p'] },
  { id: 'tool.polyline', label: 'Polyline (drawings)', group: 'markup', combos: ['shift+o'] },
  { id: 'intent.region', label: 'Change request, in palette order (applies to the selection, or draw a box)', group: 'markup', combos: DIGITS, display: '1 – 9' },
  { id: 'intent.slide', label: 'Slide request: move / delete / approve slide', group: 'markup', combos: ['shift+1', 'shift+2', 'shift+3'], display: '⇧1 – ⇧3' },
];

const PAGE_KEYS: KeyBinding[] = [
  { id: 'page.prev', label: 'Previous page / slide', group: 'navigation', combos: ['['] },
  { id: 'page.next', label: 'Next page / slide', group: 'navigation', combos: [']'] },
];

/** Miro's keys as the PDF / slides markup editors use them. */
export const EDITOR_KEYMAP: readonly KeyBinding[] = (() => {
  const out: KeyBinding[] = [];
  for (const b of BOARD_KEYMAP) {
    if (NOT_IN_EDITORS.has(b.id)) continue;
    out.push(EDITOR_LABELS[b.id] ? { ...b, label: EDITOR_LABELS[b.id]! } : b);
    if (b.id === 'app.shortcuts') out.push(...MARKUP_TOOLS);
    if (b.id === 'nav.search') out.push(...PAGE_KEYS);
  }
  return out;
})();

/** 0-based index a digit combo (`3`, `shift+2`) stands for. */
export function digitIndex(combo: string): number {
  const k = parseCombo(combo).key;
  return /^[1-9]$/.test(k) ? Number(k) - 1 : -1;
}

export function binding(id: CommandId, keymap: readonly KeyBinding[] = BOARD_KEYMAP): KeyBinding {
  const b = keymap.find((k) => k.id === id);
  if (!b) throw new Error(`unknown command ${id}`);
  return b;
}

// ---- platform -------------------------------------------------------------------

export function detectPlatform(nav: { platform?: string; userAgent?: string } | undefined = typeof navigator === 'undefined' ? undefined : navigator): Platform {
  const s = `${nav?.platform ?? ''} ${nav?.userAgent ?? ''}`;
  return /Mac|iPhone|iPad|iPod/i.test(s) ? 'mac' : 'other';
}

// ---- combos ---------------------------------------------------------------------

export interface Combo {
  key: string;
  mod: boolean;
  shift: boolean;
  alt: boolean;
  /** `?` matches whatever modifier the layout needs to type it. */
  anyShift: boolean;
}

export function parseCombo(combo: string): Combo {
  const parts = combo.toLowerCase().split('+');
  // `mod+=` style keys survive the split; a trailing empty part means the key was "+".
  let key = parts.pop() ?? '';
  if (key === '' && parts.length) {
    parts.pop();
    key = '+';
  }
  const has = (m: string) => parts.includes(m);
  return { key, mod: has('mod') || has('cmd') || has('ctrl'), shift: has('shift'), alt: has('alt') || has('option'), anyShift: key === '?' };
}

export interface KeyLike {
  key: string;
  code?: string;
  metaKey: boolean;
  ctrlKey: boolean;
  shiftKey: boolean;
  altKey: boolean;
}

/** Normalised key name: the physical letter or digit (from `code`), else `key` lower-cased. */
export function eventKey(e: KeyLike): string {
  const code = e.code ?? '';
  if (/^Key[A-Z]$/.test(code)) return code.slice(3).toLowerCase();
  if (/^Digit[0-9]$/.test(code)) return code.slice(5);
  if (code === 'Equal') return '=';
  if (code === 'Minus') return '-';
  const k = e.key.toLowerCase();
  if (k === 'esc') return 'escape';
  if (k === 'del') return 'delete';
  if (k === ' ') return 'space';
  return k;
}

export function matches(e: KeyLike, combo: Combo, platform: Platform): boolean {
  const mod = platform === 'mac' ? e.metaKey : e.ctrlKey;
  const otherCtrl = platform === 'mac' ? e.ctrlKey : e.metaKey;
  if (otherCtrl || mod !== combo.mod || e.altKey !== combo.alt) return false;
  if (combo.anyShift) return e.key === combo.key;
  if (e.shiftKey !== combo.shift) return false;
  return eventKey(e) === combo.key;
}

/** The binding (and which of its combos) a key event triggers, if any. */
export function resolve(e: KeyLike, platform: Platform, keymap: readonly KeyBinding[] = BOARD_KEYMAP): { binding: KeyBinding; combo: string } | null {
  for (const b of keymap) {
    for (const c of b.combos) {
      if (matches(e, parseCombo(c), platform)) return { binding: b, combo: c };
    }
  }
  return null;
}

const GLYPH: Record<string, string> = {
  arrowup: '↑',
  arrowdown: '↓',
  arrowleft: '←',
  arrowright: '→',
  pageup: 'PgUp',
  pagedown: 'PgDn',
  escape: 'Esc',
  enter: '↵',
  tab: 'Tab',
  delete: 'Del',
  backspace: '⌫',
  space: 'Space',
};

export function formatCombo(combo: string, platform: Platform): string {
  const c = parseCombo(combo);
  const key = GLYPH[c.key] ?? (c.key.length === 1 ? c.key.toUpperCase() : c.key.toUpperCase());
  if (platform === 'mac') return `${c.mod ? '⌘' : ''}${c.alt ? '⌥' : ''}${c.shift ? '⇧' : ''}${key}`;
  return [c.mod && 'Ctrl', c.alt && 'Alt', c.shift && 'Shift', key].filter(Boolean).join('+');
}

/** What the cheat sheet / tooltips show for a binding. */
export function describe(b: KeyBinding, platform: Platform): string {
  if (b.display) return platform === 'mac' ? b.display.replace(/\balt\b/g, '⌥') : b.display.replace(/\balt\b/g, 'Alt');
  return b.combos.map((c) => formatCombo(c, platform)).join(' / ');
}

/** Tooltip suffix, e.g. "Sticky note — N". */
export function withKey(label: string, id: CommandId, platform: Platform = detectPlatform(), keymap: readonly KeyBinding[] = BOARD_KEYMAP): string {
  return `${label} — ${describe(binding(id, keymap), platform)}`;
}

// ---- typing guard -------------------------------------------------------------------

const NON_TEXT_INPUTS = new Set(['button', 'checkbox', 'color', 'file', 'hidden', 'image', 'radio', 'range', 'reset', 'submit']);

interface ElementLike {
  tagName?: string;
  type?: string;
  isContentEditable?: boolean;
  readOnly?: boolean;
  closest?: (sel: string) => unknown;
}

/** True while the user is typing: inputs, textareas, selects, contenteditable (Excalidraw's text editor). */
export function isTypingTarget(target: unknown): boolean {
  const el = target as ElementLike | null;
  if (!el || typeof el !== 'object') return false;
  const tag = (el.tagName ?? '').toUpperCase();
  if (tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (tag === 'INPUT') return !NON_TEXT_INPUTS.has((el.type ?? 'text').toLowerCase());
  if (el.isContentEditable) return true;
  return !!el.closest?.('[contenteditable=""], [contenteditable="true"], [contenteditable="plaintext-only"]');
}

// ---- dispatcher ------------------------------------------------------------------------

/**
 * Return `false` to let the event through (library default), anything else
 * consumes it (preventDefault + stopPropagation).
 */
export type CommandHandler = (e: KeyboardEvent, combo: string) => boolean | void;
export type CommandHandlers = Partial<Record<CommandId, CommandHandler>>;

export interface KeymapOptions {
  platform?: Platform;
  keymap?: readonly KeyBinding[];
  /** Skip everything (e.g. while a modal of ours is open, or the board is read-only). */
  enabled?: () => boolean;
  /** Commands allowed while typing (default none). */
  whileTyping?: CommandId[];
  /**
   * Sees every key before the keymap (and before the canvas library): return true when it consumed the
   * key (it stops propagation itself). A modal tool (the board's Zone tool) pauses or redirects keys here.
   */
  intercept?: (e: KeyboardEvent) => boolean;
}

/** Events the app dispatches itself (to reach a library's own handler) carry this flag. */
export const SYNTHETIC = '__rlKeymapSynthetic';

export function createKeymapListener(handlers: CommandHandlers, opts: KeymapOptions = {}, consumed?: Set<string>): (e: KeyboardEvent) => void {
  const platform = opts.platform ?? detectPlatform();
  const keymap = opts.keymap ?? BOARD_KEYMAP;
  return (e: KeyboardEvent) => {
    if ((e as unknown as Record<string, unknown>)[SYNTHETIC] || e.defaultPrevented || e.isComposing) return;
    if (opts.intercept?.(e)) return;
    if (opts.enabled && !opts.enabled()) return;
    const hit = resolve(e, platform, keymap);
    if (!hit) return;
    const handler = handlers[hit.binding.id];
    if (!handler) return;
    const typing = isTypingTarget(e.target) || isTypingTarget(typeof document === 'undefined' ? null : document.activeElement);
    if (typing && !(opts.whileTyping ?? []).includes(hit.binding.id)) return;
    if (handler(e, hit.combo) === false) return;
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();
    consumed?.add(e.code || e.key);
  };
}

/**
 * Listen on `window` in the capture phase, ahead of Excalidraw. The key-up of
 * a key we handled is swallowed too, so the library never sees half a
 * keystroke. Returns the unsubscribe.
 */
export function installKeymap(handlers: CommandHandlers, opts: KeymapOptions = {}, target: Window = window): () => void {
  const consumed = new Set<string>();
  const down = createKeymapListener(handlers, opts, consumed);
  const up = (e: KeyboardEvent) => {
    const k = e.code || e.key;
    if (!consumed.delete(k) || (e as unknown as Record<string, unknown>)[SYNTHETIC]) return;
    e.stopPropagation();
    e.stopImmediatePropagation();
  };
  target.addEventListener('keydown', down, { capture: true });
  target.addEventListener('keyup', up, { capture: true });
  return () => {
    target.removeEventListener('keydown', down, { capture: true });
    target.removeEventListener('keyup', up, { capture: true });
  };
}

/** Dispatch a key event the keymap ignores, so a library's own shortcut handles it. */
export function dispatchNativeKey(target: EventTarget, init: KeyboardEventInit): void {
  const ev = new KeyboardEvent('keydown', { bubbles: true, cancelable: true, ...init });
  Object.defineProperty(ev, SYNTHETIC, { value: true });
  target.dispatchEvent(ev);
}

/** Arrow combo → unit direction. */
export function arrowDirection(combo: string): { dx: number; dy: number } {
  const k = parseCombo(combo).key;
  return { dx: k === 'arrowleft' ? -1 : k === 'arrowright' ? 1 : 0, dy: k === 'arrowup' ? -1 : k === 'arrowdown' ? 1 : 0 };
}

/** Reading order for Tab cycling: rows (tolerance = half the smaller height), then left to right. */
export function readingOrder<T extends { x: number; y: number; h: number }>(items: T[]): T[] {
  const sorted = [...items].sort((a, b) => a.y - b.y || a.x - b.x);
  const rows: T[][] = [];
  for (const it of sorted) {
    const row = rows[rows.length - 1];
    if (row && Math.abs(it.y - row[0].y) <= Math.min(it.h, row[0].h) / 2) row.push(it);
    else rows.push([it]);
  }
  return rows.flatMap((r) => r.sort((a, b) => a.x - b.x));
}

/** Next index in a cyclic list (from `current`, -1 = none). */
export function cycleIndex(length: number, current: number, backwards: boolean): number {
  if (length === 0) return -1;
  if (current < 0) return backwards ? length - 1 : 0;
  return (current + (backwards ? length - 1 : 1)) % length;
}
